import React from 'react';
import styled from 'styled-components';
import { device, Dropdown_ } from 'monica-alexandria';
import { read_merge_value } from '../../../utils/merge/merge_formatting';

const MergePreview = styled.div`
    display: grid;
    gap: 2rem;
    min-width: 0;
`;

const MergePreviewCard = styled.div`
    display: grid;
    gap: 1rem;
    padding: var(--normalPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    color: ${p => p.theme.color};

    p, h5, h6 {
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }
`;

const MergePreviewValues = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;

    @media ${device.sm} {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const MergePreviewValue = styled.div`
    display: grid;
    align-content: start;
    gap: 0.4rem;
`;

export default function Merge_Preview(props) {
    const conflicts = props.preview.fields.filter(field => field.conflict);
    const automaticFields = props.preview.fields.filter(field => !field.conflict);
    const sources = props.preview.sources || [props.preview.source];
    const records = [props.preview.survivor, ...sources];
    const fieldOptions = field => field.options || [
        { recordId: 'survivor', label: props.t('Record to keep'), value: field.survivorValue },
        { recordId: 'source', label: props.t('Duplicate'), value: field.sourceValue }
    ];

    return (
        <MergePreview>
            <MergePreviewCard>
                <Dropdown_ label={props.t('Record to keep')}>
                    <select aria-label={props.t('Record to keep')} value={props.preview.survivor.id} disabled={props.disabled || props.lockSurvivor} onChange={event => props.onSurvivorChange(event.target.value)}>
                        {records.map(record => <option key={record.id} value={record.id}>{record.label} · {record.id}</option>)}
                    </select>
                </Dropdown_>
                {props.lockSurvivor ? <p>{props.t('A registered selected lead is kept to preserve its account identity; otherwise, the oldest selected lead is kept.')}</p> : null}
                <p>{props.preview.survivor.label} · {props.preview.survivor.id}</p>
                <h5>{props.t(sources.length > 1 ? 'Duplicates to absorb' : 'Duplicate to absorb')}</h5>
                {sources.map(source => <p key={source.id}>{source.label} · {source.id}</p>)}
                <p>{props.t(sources.length > 1 ? 'The duplicates will leave the active lists. Their history and related records will be retained on the surviving record.' : 'The duplicate will leave the active lists. Its history and related records will be retained on the surviving record.')}</p>
                {props.preview.entityType === 'lead' ? <p>{props.t('All brands are retained separately. Merge duplicate brands after merging their leads.')}</p> : null}
            </MergePreviewCard>
            <MergePreviewCard>
                <h4>{props.t('Records retained')}</h4>
                <MergePreviewValues>
                    <MergePreviewValue><h5>{props.t('Brands')}</h5><p>{props.preview.summary.brands}</p></MergePreviewValue>
                    <MergePreviewValue><h5>{props.t('Assets')}</h5><p>{props.preview.summary.assets}</p></MergePreviewValue>
                    <MergePreviewValue><h5>{props.t('Comments')}</h5><p>{props.preview.summary.comments}</p></MergePreviewValue>
                    <MergePreviewValue><h5>{props.t('Timeline events')}</h5><p>{props.preview.summary.timelineEvents}</p></MergePreviewValue>
                </MergePreviewValues>
            </MergePreviewCard>
            {props.preview.blockers.map(blocker => (
                <MergePreviewCard role="alert" key={blocker.code}>
                    <h5>{props.t('Merge unavailable')}</h5>
                    <p>{props.t(blocker.message)}</p>
                </MergePreviewCard>
            ))}
            <h4>{props.t('Choose which values to keep')}</h4>
            {conflicts.length === 0 ? <p>{props.t('No conflicting values need a decision.')}</p> : null}
            {conflicts.map(field => (
                <MergePreviewCard key={field.key}>
                    <h5>{props.t(field.label)}</h5>
                    <MergePreviewValues>
                        {fieldOptions(field).map(option => (
                            <MergePreviewValue key={option.recordId}><h6>{option.label}{field.options ? ` · ${option.recordId}` : ''}</h6><p>{read_merge_value(option.value, props.t)}</p></MergePreviewValue>
                        ))}
                    </MergePreviewValues>
                    <Dropdown_ label={props.t('Value to keep')}>
                        <select aria-label={props.t(field.label)} value={props.choices[field.key] || ''} disabled={props.disabled} onChange={event => props.onChoice(field.key, event.target.value)}>
                            <option value="">{props.t('Choose a value')}</option>
                            {fieldOptions(field).map(option => <option key={option.recordId} value={option.recordId}>{read_merge_value(option.value, props.t)} · {option.label}{field.options ? ` · ${option.recordId}` : ''}</option>)}
                        </select>
                    </Dropdown_>
                </MergePreviewCard>
            ))}
            <MergePreviewCard>
                <h4>{props.t('Values kept automatically')}</h4>
                {automaticFields.map(field => (
                    <MergePreviewValue key={field.key}><h5>{props.t(field.label)}</h5><p>{read_merge_value(field.value, props.t)}</p></MergePreviewValue>
                ))}
            </MergePreviewCard>
        </MergePreview>
    );
}
